import { Tooltip, IconButton, Card, CardContent, CardHeader, Drawer, useMediaQuery, Box, Button, FormHelperText, 
  Grid, MenuItem, Select, Typography, FormControl, FormControlLabel, Checkbox, Chip, InputLabel, alpha} from "@mui/material";
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import { Dispatch, FC, ReactElement, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import CreateOrEdit from "@/controller/CreateOrEdit";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { useResourceRegister } from "@/utils/resourceRegister";
import Create from "@/controller/Create";
import { Refetch, useListController, useSaveContext } from "ra-core";
import { DatabaseResponseDto, DatabaseType } from "@/gen/api/backup";
import { OptionResponseDto } from "@/gen/api/encrypt";
import { EncryptSettingsCopyAnalyseInfoDto, EncryptSettingsCopyInput, TableSchemaResponseDto } from "@/gen/api/governance";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { useMutateEncryptionMappingsContext } from "@/pages/obfuscation/mutateEncryptionMappingsContext";



const resource = 'copy-table-mappings'

interface CopyTableMappingsProps {
  destDatabaseName: string,
  destTable: TableSchemaResponseDto,
  tbSelectRefetch: Refetch,
}

const CopyTableMappings: FC<CopyTableMappingsProps> = (props) => {
  useResourceRegister({ name: resource })
  useResourceRegister({ name: 'settings-select-databases' })
  useResourceRegister({ name: 'settings-select-tables' })

  return (
    <CopyTableMappingsBody {...props} />
  )
}


interface States {
  open: boolean;
  sourceDatabaseId: number;
  sourceTableId: number | "";
  includeObfs: boolean;
  validatedResult: EncryptSettingsCopyAnalyseInfoDto | null;
  isSubmitting: boolean,
  errors: string|undefined,
}
const currCacheKey = (states:States) => `${states.sourceDatabaseId}|${states.sourceTableId}|${states.includeObfs}`

const CopyTableMappingsBody: FC<CopyTableMappingsProps> = ({ destDatabaseName, destTable, tbSelectRefetch }) => {
  const [states, setStates] = useState<States>({
    open: false,
    sourceDatabaseId: destTable.databaseId!,
    sourceTableId: '',
    includeObfs: false,
    validatedResult: null,
    isSubmitting: false,
    errors: undefined,
  })
  const resultCacheRef = useRef({} as Record<string, EncryptSettingsCopyAnalyseInfoDto>)

  const handleClose = (): void => {
    setStates(v => ({...v, open: false}));
  };
  
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  useEffect(() => {
    setStates(v => ({
      ...v, 
      sourceDatabaseId: destTable.databaseId!, 
      sourceTableId: '', 
      validatedResult: null, 
      isSubmitting: false, 
      errors: undefined,
    }))
    resultCacheRef.current = {}
  }, [destTable])


  const allDatabases : Record<string, DatabaseResponseDto> = useListItems({ resource: 'databases', filter: {} }, idFn)
  const dbsWithSchema : OptionResponseDto[] = useListItems({ resource: 'settings-select-databases', filter: {} }, sortByText)
  const groupsByDBType = useMemo(() => {
    return dbsWithSchema.reduce((acc: Record<DatabaseType, DatabaseResponseDto[]>, x) => {
      const db = allDatabases[x.key!]
      if (!db) { return acc }
      const dt = db.databaseType!
      const arr = acc[dt] ?? []
      arr.push(db)
      acc[dt] = arr
      return acc
    }, {} as Record<DatabaseType, DatabaseResponseDto[]>)
  }, [allDatabases, dbsWithSchema])
  const tablesOfSrcDB : OptionResponseDto[] = useListItems({ resource: 'settings-select-tables', filter: states.sourceDatabaseId && { databaseId: states.sourceDatabaseId } }, sortByText)
  
  const destDatabase : DatabaseResponseDto|undefined = allDatabases[destTable.databaseId!]

  return (
    <>
      <Tooltip title="Copy Mappings">
        <IconButton size="small" onClick={() => setStates(v => ({...v, open: true}))}>
          <FormatPaintIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Drawer
        anchor="right"
        onClose={handleClose}
        open={states.open}
        PaperProps={{ sx: { p: 2, width: width, }}}
      >
        <Card sx={{ overflowY: 'auto', mb: 4, }}>
          <CardHeader title="Copy Table Mappings" />
          <CardContent>
            <Box sx={{ '& .MuiTypography-body2': { mb: 2 } }}>
              <Typography variant="body2">
                <Typography variant="caption" color="InactiveCaptionText">{destDatabaseName}{'. '}{destTable.schema}{'. '}</Typography>
                {destTable.name}
              </Typography>
              <Typography variant="body2">Copy from:</Typography>
            </Box>
            <Grid container spacing={4} >
              <Grid item xs={12} >
                <FormControl fullWidth>
                  <InputLabel id="SourceDatabase-label">Source Database*</InputLabel>
                  <Select fullWidth
                    label="Source Database*" 
                    labelId="SourceDatabase-label" 
                    id="SourceDatabase" 
                    name="SourceDatabase" 
                    value={states.sourceDatabaseId}
                    onChange={(e) => {
                      const sourceDatabaseId = parseInt(e.target.value as string)
                      if (!isNaN(sourceDatabaseId)) {
                        setStates(v => ({...v, sourceDatabaseId, sourceTableId: '' }))
                      }
                    }}
                  >
                    {destDatabase && (groupsByDBType[destDatabase.databaseType!] ?? []).map(db => (
                      <MenuItem key={db.databaseId} value={(db.databaseId || '') as any} >{db.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} >
                <FormControl fullWidth>
                  <InputLabel id="SourceTable-label">Source Table*</InputLabel>
                  <Select fullWidth 
                    label="Source Table*" 
                    labelId="SourceTable-label" 
                    id="SourceTable" 
                    name="SourceTable" 
                    value={states.sourceTableId}
                    onChange={(e) => {
                      const sourceTableId = parseInt(e.target.value as string)
                      if (!isNaN(sourceTableId)) {
                        setStates(v => {
                          const v1 = {...v, sourceTableId }
                          v1.validatedResult = resultCacheRef.current[currCacheKey(v1)] ?? null
                          return v1
                        })
                      }
                    }}
                  >
                    {tablesOfSrcDB.map(tb => (
                      <MenuItem key={tb.key} value={tb.key as any}>{tb.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl>
                  <FormControlLabel 
                    control={
                      <Checkbox color="primary" name="IncludeObfs" 
                        checked={states.includeObfs} 
                        onChange={(e) => setStates(v => {
                          const v1 = {...v, includeObfs: e.target.checked }
                          v1.validatedResult = resultCacheRef.current[currCacheKey(v1)] ?? null
                          return v1
                        })} 
                      />
                    }
                    label="Include Obfuscation Rule Mappings"
                  />
                </FormControl>
              </Grid>
            </Grid>
            {states.errors && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {states.errors}
                </FormHelperText>
              </Box>
            )}
            {states.validatedResult &&
              <>
                <Typography variant="body2" sx={{ mt: 2, }}>
                  <b>{states.validatedResult.matchedMappedCounts! + states.validatedResult.matchedSkipedCounts!}</b> matches found. {" "}
                  <Typography variant="caption" color="InactiveCaptionText">
                    (
                      {states.validatedResult.matchedMappedCounts} mapped{" / "}
                      {states.validatedResult.matchedSkipedCounts} skipped
                      {/* {!states.includeObfs ? "" :
                        ` / ${states.validatedResult.matchedObfsCounts} obfuscation rules`} */}
                    )
                  </Typography>
                </Typography>
                <Box 
                  sx={{
                    p: 1, mt: 2,
                    bgcolor: theme => alpha(theme.palette.warning.light, 0.1),
                    border: theme => `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                    borderRadius: '8px',
                    '& p.MuiTypography-body2:not(:first-of-type)': { mt: 1.5 },
                  }}
                >
                  <Typography component="p" variant="body2"><b>Note:</b> Any existing configuration of the fields to be copied over will be overwritten.</Typography>
                  {states.validatedResult?.matchedCounts === 0 &&
                    <Typography component="p" variant="body2">None of the source table fields matches destination table fields. Copy Mapping can not be performed.</Typography>}
                  {states.validatedResult.notMatchedOfSource!.length > 0 &&
                    <>
                      <Typography component="p" variant="body2">The following fields of source table do not have match in the destiniation table. ({states.validatedResult?.notMatchedOfSource?.length} out of {states.validatedResult?.hasMappingCountOfSource})</Typography>
                      <Box sx={{ 
                        maxHeight: 300,
                        overflowY: 'auto',
                        '& .MuiChip-root': { mr: 1, mt: 1, },
                      }}>
                        {states.validatedResult?.notMatchedOfSource?.map(s => (
                          <Chip key={s} label={s} />
                        ))}
                      </Box>
                    </>}
                </Box>
              </>
            }
          </CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
            <Button
              color="primary"
              disabled={states.isSubmitting}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            {!states.validatedResult &&
              <ValidateAction 
                destTable={destTable} 
                states={states} 
                setStates={setStates} 
                updateCache={(k,v) => { resultCacheRef.current[k] = v }} 
              />
            }
            {states.validatedResult &&
              <CopyAction 
                destTable={destTable} 
                states={states} 
                setStates={setStates} 
                tbSelectRefetch={tbSelectRefetch}
              />
            }
          </Box>
        </Card>
      </Drawer>

    </>
  )
}

export default CopyTableMappings;


const idFn = (x:any) => x
const sortByText = (x:any[]) => Object.values(x).sort((a, b) => a.text < b.text ? -1 : 1)
const defaultTransform = (x:any) => idFn(Object.values(x))
const useListItems = ({resource, filter}: { resource: string, filter: any }, tranform?: (x: any) => any) => {
  const { data } = useListController({ 
    resource: filter ? resource : 'noop', 
    basePath: '', 
    filter: filter || {}, 
    options: { action: 'CP_TB_MPS' },
  })

  const tranform1 = tranform || defaultTransform
  return useMemo(() => tranform1(data || {}), [data, tranform1])
}

const ValidateAction : FC<{
  destTable:TableSchemaResponseDto, 
  states:States, 
  setStates:Dispatch<SetStateAction<States>>, 
  updateCache: (a:string, b:any)=>void,
}> = ({ destTable, states, setStates, updateCache, }) => {
  const onSuccess = ({data}: {data:EncryptSettingsCopyAnalyseInfoDto}) => {
    setStates(v => {
      const v1 = {...v, 
        isSubmitting: false,
        validatedResult: data,
        errors: undefined,
      }
      updateCache(currCacheKey(v1), data)
      return v1
    })
  }
  const onFailure = (err:any) => {
    const errMsg = retrieveAxiosErr(err)
    setStates(v => ({...v,
      isSubmitting: false,
      validatedResult: null,
      errors: errMsg
    }))
  }

  return (
    <CreateOrEdit resource={resource} basePath="" record={states} {...{ onSuccess, onFailure }}>
      <WithSave 
        render={({ save, saving }) => 
          <Button
            color="primary"
            disabled={!states.sourceDatabaseId || !states.sourceTableId || saving}
            type="submit"
            variant="contained"
            onClick={() => {
              setStates(v => ({...v, isSubmitting: true}))
              const payload : EncryptSettingsCopyInput = {
                sourceDatabaseId: states.sourceDatabaseId, 
                sourceTableId: states.sourceTableId as number, 
                destDatabaseId: destTable.databaseId!, 
                destTableId: destTable.tableId!,
                includeObfs: states.includeObfs,
              }
              save(payload)
            }}
          >
            Validate
          </Button>
        }
      />
    </CreateOrEdit>
  )
}

const CopyAction : FC<{
  destTable:TableSchemaResponseDto, 
  states:States, 
  setStates:Dispatch<SetStateAction<States>>, 
  tbSelectRefetch: Refetch,
}> = ({ destTable, states, setStates, tbSelectRefetch }) => {
  const handlers = useDefaultSavedHandlers("Copy Table Mappings")
  const { fieldListCtrl: { refetch } } = useMutateEncryptionMappingsContext()
  handlers.onSuccess.merge(refetch)
  handlers.onSuccess.merge(tbSelectRefetch)
  const { onSuccess, onFailure } = useMemo(() => {
    return {
      onSuccess: () => {
        setStates(v => ({...v, isSubmitting: false, errors: undefined, open: false }))
      },
      onFailure: () => {
        setStates(v => ({...v, isSubmitting: false, errors: undefined }))
      },
    }
  }, [setStates])
  handlers.onSuccess.merge(onSuccess)
  handlers.onFailure.merge(onFailure)

  return (
    <Create resource={resource} basePath="" {...handlers}>
      <WithSave 
        render={({ save, saving }) => 
          <Button
            color="primary"
            disabled={states.validatedResult?.matchedCounts === 0 || saving}
            type="submit"
            variant="contained"
            onClick={() => {
              setStates(v => ({...v, isSubmitting: true}))
              const payload : EncryptSettingsCopyInput = {
                sourceDatabaseId: states.sourceDatabaseId, 
                sourceTableId: states.sourceTableId as number, 
                destDatabaseId: destTable.databaseId!, 
                destTableId: destTable.tableId!,
                includeObfs: states.includeObfs,
              }
              save(payload)
            }}
          >
            Copy
          </Button>
        }
      />
    </Create>
  )
}

const WithSave : FC<{render: (x:{save:(record: any, callbacks?: any) => void, saving:boolean}) => ReactElement|null}> = ({ render }) => {
  const save = useSimpleSaveAction()
  const { saving } = useSaveContext()
  return render({ save, saving: Boolean(saving) })
}






